import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Progress } from './components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { Checkbox } from './components/ui/checkbox';
import { Textarea } from './components/ui/textarea';
import { 
  Car, Upload, FileText, Download, AlertTriangle, CheckCircle, 
  Gauge, Flame, Activity, Clock, TrendingUp, Zap, Settings,
  Usb, CircleAlert, ExternalLink, Cable, Radio, MessageSquare, Send,
  GitCompare, ArrowRight, ChevronUp, CreditCard, Bot, ShieldCheck, BookOpen, Eye, EyeOff,
  Telescope, Trash2, Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LiveDataLogger from './components/LiveDataLogger';
// Legacy scattered landing sections replaced by unified UniversalHome + UniversalAbout
// (HeroSection, StatsCounter, ServicesShowcase, ProcessSteps, PortfolioGrid,
//  ProblemSection, DifferentiatorSection, Testimonials, FinalCTA, AboutPage, PartnerPage)
// preserved in /src/components/ but no longer rendered.
import UniversalHome from './components/UniversalHome';
import UniversalAbout from './components/UniversalAbout';
import Footer from './components/Footer';
// NOTE: MapViewer + ECUDefinitions intentionally hidden from public UI —
// moved to src/build_later/. To re-enable, import from '../build_later/...'
// and restore the tabs below.
import TuneCompare from './components/TuneCompare';
import FlashECUTab from './components/FlashECUTab';
import DatalogAnalysisView from './components/DatalogAnalysisView';
import GenerateTuneFromDatalog from './components/GenerateTuneFromDatalog';
import LoginPage from './components/LoginPage';
import CustomerDashboard from './components/CustomerDashboard';
import AITuneIterator from './components/AITuneIterator';
import VWFlashPrep from './components/VWFlashPrep';
import AIAssistant from './components/AIAssistant';
import BinUpload from './components/BinUpload';
import DatalogCharts from './components/DatalogCharts';
import VanosScorecard from './components/VanosScorecard';
import TuneTimeline from './components/TuneTimeline';
import TuneRecommender from './components/TuneRecommender';
import HealthCheck from './components/HealthCheck';
import MechanicalGuide from './components/MechanicalGuide';
import PaywallGate from './components/PaywallGate';
import FreeAnalysisModal from './components/FreeAnalysisModal';
import ClaudeFAB from './components/ClaudeFAB';
import KBAnalyticsPanel from './components/KBAnalyticsPanel';
import AdminBetaApplicationsPanel from './components/AdminBetaApplicationsPanel';
import TrialExpiredModal from './components/TrialExpiredModal';
import PageViewsPanel from './components/PageViewsPanel';
import ExternalResourcesPanel from './components/ExternalResourcesPanel';import ModOrdersPanel from './components/ModOrdersPanel';
import A2LMapSearchPanel from './components/A2LMapSearchPanel';
import CurationPanel from './components/CurationPanel';
import RegisterDataloggerPanel from './components/RegisterDataloggerPanel';import { usePageViewTracker } from './hooks/usePageViewTracker';
import XDFLibraryPanel from './components/XDFLibraryPanel';
import WalletBalanceBadge from './components/WalletBalanceBadge';
import TopUpModal from './components/TopUpModal';
import TesterActivityPanel from './components/TesterActivityPanel';
import BlogAdminPanel from './components/BlogAdminPanel';
import MapContributionPanel from './components/MapContributionPanel';
import GreenLightTune from './components/GreenLightTune';
import MegaSquirtDiagnose from './components/MegaSquirtDiagnose';
import SmartDataloggerPage from './components/SmartDataloggerPage';
import MapDiscoveryPage from './components/MapDiscoveryPage';
import VoteSharePage from './components/VoteSharePage';
import StoryPage from './components/StoryPage';
import BlogListPage from './components/BlogListPage';
import BlogPostPage from './components/BlogPostPage';
import ContestBanner from './components/ContestBanner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Download helper
const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Download error:', error);
    alert('Download failed. Please try again.');
  }
};

// Credit-gated download helper
const downloadWithCredit = async (filename, user, onCreditUpdate) => {
  if (!user) {
    alert('Please sign in to download tunes.');
    return;
  }
  const isUnlimited = user.is_unlimited || user.role === 'admin';
  if (!isUnlimited && (user.credits || 0) < 1) {
    alert('Not enough credits. Purchase a tuning package first.');
    return;
  }
  if (!isUnlimited && !window.confirm(`This will use 1 credit (${user.credits} remaining). Continue?`)) return;

  try {
    const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
    const res = await fetch(`${API_URL}/api/tunes/use-credit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ filename }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Download failed' }));
      alert(err.detail || 'Download failed');
      return;
    }
    const data = await res.json();
    if (onCreditUpdate) onCreditUpdate(data.credits_remaining);
    await downloadFile(`${API_URL}${data.download_url}`, filename);
  } catch (err) {
    if (process.env.NODE_ENV === 'development') console.error('Download-with-credit failed:', err);
    alert('Download failed. Please try again.');
  }
};

const CUSTOMER_VEHICLE = {
  year: 2010,
  make: "BMW",
  model: "328xi E90",
  engine: "N51",
  ecu_type: "MSV80",
  vin: "A652287",
  mods: ["Stage 2 Tune", "Rear O2 Delete", "Cat Delete", "SAI Delete", "EGR Delete", "EVAP Delete"]
};

// ===== STICKY HEADER =====
function SiteHeader({ showPortal, onLogoClick, onPortalClick, vehicle, onShowLogin, onShowTopUp }) {
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  // Listen for global "open top-up modal" events (fired by ClaudeFAB on 402).
  useEffect(() => {
    if (!onShowTopUp) return;
    const onOpen = () => onShowTopUp();
    window.addEventListener('wallet:openTopUp', onOpen);
    return () => window.removeEventListener('wallet:openTopUp', onOpen);
  }, [onShowTopUp]);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
        scrolled ? 'bg-[#050505]/95 backdrop-blur-md border-b border-[#27272A]' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
      data-testid="site-header"
    >
      {/* Full-width branded banner — widescreen 3:1 logo fills the page edge-to-edge */}
      <button
        onClick={onLogoClick}
        className="block w-full relative overflow-hidden group bg-black"
        data-testid="header-logo"
      >
        <img
          src="/brand/ai-tuner-pro-logo-wide.jpg"
          alt="AI Tuner Pro"
          className="w-full h-32 sm:h-40 md:h-48 lg:h-56 object-cover object-center transition-transform group-hover:scale-[1.01]"
          loading="eager"
        />
      </button>

      {/* Controls row — centered under the banner */}
      <div className="container mx-auto px-6 py-3">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 w-full">
          <nav className="hidden lg:flex items-center gap-7">
            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-how-it-works"
              className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">How It Works</button>
            <button onClick={() => document.getElementById('scope')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-scope"
              className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">Scope</button>
            <button onClick={() => document.getElementById('hardware')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-hardware"
              className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">Hardware</button>
            <button onClick={() => document.getElementById('roadmap')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-roadmap"
              className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">Roadmap</button>
            <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-pricing"
              className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">Pricing</button>
            <button onClick={onPortalClick}
              data-testid="nav-portal"
              className="text-sm text-zinc-400 hover:text-white transition-colors font-medium">Portal</button>
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <div className="hidden sm:flex items-center gap-2 bg-[#111111] border border-[#27272A] px-3 py-1.5 rounded-sm">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#FF5E1A]/30 flex items-center justify-center text-[10px] font-bold text-[#FF5E1A]">
                      {user.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <span className="text-xs text-white font-medium max-w-[100px] truncate">{user.name}</span>
                  {user.is_unlimited || user.role === 'admin' ? (
                    <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-['JetBrains_Mono'] uppercase tracking-wider rounded-sm" data-testid="unlimited-badge">
                      Unlimited
                    </span>
                  ) : (
                    <span className="text-xs text-[#FF5E1A] font-['JetBrains_Mono']" data-testid="credits-badge">{user.credits} cr</span>
                  )}
                </div>
                <button onClick={onPortalClick} data-testid="header-enter-portal-btn"
                  className="px-5 py-2 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.1em] uppercase rounded-sm transition-all">
                  {showPortal ? 'Dashboard' : 'My Garage'}
                </button>
                <WalletBalanceBadge onClickTopUp={() => onShowTopUp && onShowTopUp()} />
                <button onClick={logout} data-testid="header-logout-btn"
                  className="px-3 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white text-xs font-bold uppercase rounded-sm transition-all">
                  Out
                </button>
              </>
            ) : (
              <>
                <button onClick={onShowLogin} data-testid="header-login-btn"
                  className="px-4 py-2 border border-zinc-700 hover:border-[#FF5E1A] text-white text-xs font-bold tracking-[0.1em] uppercase rounded-sm transition-all">
                  Sign In
                </button>
                <button onClick={onPortalClick} data-testid="header-enter-portal-btn"
                  className="px-5 py-2 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.1em] uppercase rounded-sm transition-all">
                  {showPortal ? 'Dashboard' : 'Start Tuning'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// ===== MAIN APP =====
function App() {
  const [vehicle, setVehicle] = useState(null);
  const [datalogs, setDatalogs] = useState([]);
  const [tunes, setTunes] = useState([]);
  const [selectedDatalog, setSelectedDatalog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPortal, setShowPortal] = useState(false);
  const [showFreeAnalysis, setShowFreeAnalysis] = useState(false);
  const [trialExpired, setTrialExpired] = useState(null); // payload from 402
  useEffect(() => {
    // Global 402 trial-expired interceptor — listens to window event fired by
    // any component making fetch calls; shows the upgrade modal centrally.
    const onTrialExpired = (e) => setTrialExpired(e.detail || { message: 'Your trial has ended.' });
    window.addEventListener('trial-expired', onTrialExpired);
    return () => window.removeEventListener('trial-expired', onTrialExpired);
  }, []);
  useEffect(() => {
    // Global escape hatch so any component can trigger the 3-file health check
    // without prop-drilling through the whole tree
    window.openHealthCheck = () => setShowFreeAnalysis(true);
    return () => { try { delete window.openHealthCheck; } catch { /* ignore */ } };
  }, []);

  // Listen for "open-derive-from-analysis" — fired by DatalogAnalysisView's
  // "Generate Revision Tune" button. Switch to the My Tunes tab; TuneTimeline
  // listens for the same event and opens its Derive dialog with the prefill.
  useEffect(() => {
    const onOpenDerive = () => setActiveTab('tunes');
    window.addEventListener('open-derive-from-analysis', onOpenDerive);
    return () => window.removeEventListener('open-derive-from-analysis', onOpenDerive);
  }, []);
  useEffect(() => {
    // Live Data → "Send to AI Iterate" handoff: switch to the AI Iterate tab.
    const onLiveLog = () => setActiveTab('ai-iterate');
    window.addEventListener('live-log-to-iterate', onLiveLog);
    return () => window.removeEventListener('live-log-to-iterate', onLiveLog);
  }, []);
  const [showLogin, setShowLogin] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpReason, setTopUpReason] = useState(null);
  useEffect(() => {
    // Listen for "open top-up" events fired by ClaudeFAB on 402 insufficient_credits.
    const onOpenTopUp = (e) => {
      setTopUpReason(e?.detail?.message || null);
      setTopUpOpen(true);
    };
    window.addEventListener('wallet:openTopUp', onOpenTopUp);
    return () => window.removeEventListener('wallet:openTopUp', onOpenTopUp);
  }, []);
  const { user, checkAuth, addVehicle } = useAuth();
  
  // Auto-open login for OAuth callback
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setShowLogin(true);
    }
  }, []);

  // Auto-claim pending free-analysis session after user signs in
  const [claimBanner, setClaimBanner] = useState(null);
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem('pending_free_analysis_session');
    if (!pending) return;
    (async () => {
      try {
        const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
        const res = await fetch(`${API_URL}/api/free-analysis/claim/${encodeURIComponent(pending)}`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        localStorage.removeItem('pending_free_analysis_session');
        if (res.ok) {
          const data = await res.json();
          setClaimBanner({
            bin: data.bin_filename,
            datalogs: data.datalogs?.length || 0,
            vehicle: data.vehicle_id,
          });
          // Auto-select the Upload tab so they see their pre-loaded bin/datalogs
          setActiveTab('upload');
          setShowPortal(true);
          // Reload tunes/datalogs so new entries render
          setTimeout(() => { loadTunes(); }, 300);
        }
      } catch {
        localStorage.removeItem('pending_free_analysis_session');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [selectedTuneForFlash, setSelectedTuneForFlash] = useState(null);
  const portalRef = useRef(null);

  const loadDatalogs = useCallback(async (vehicleId) => {
    try {
      const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      const response = await fetch(`${API_URL}/api/datalogs/${vehicleId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const data = await response.json();
      setDatalogs(Array.isArray(data) ? data : []);
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error loading datalogs:', error); }
  }, []);

  const loadTunes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/bin/files`);
      const binFiles = await response.json();
      const seen = new Set();
      setTunes((Array.isArray(binFiles) ? binFiles : [])
        .filter(f => f.filename.includes('Stage2') || f.filename.includes('stage2') || f.filename.includes('E85') || f.filename.includes('BMW'))
        .filter(f => (seen.has(f.filename) ? false : seen.add(f.filename))));
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error loading tunes:', error); }
  }, []);

  const initializeVehicle = useCallback(async () => {
    // Vehicle persistence is owned by the user record in MongoDB via
    // /api/auth/vehicles (see auth.py). The legacy /api/vehicles endpoint
    // creates orphans that don't survive sign-out. Always read from auth.
    try {
      if (!user) {
        // No auth → no persisted vehicle, just stage the default in-memory so
        // unauthenticated landing flows still see the showcase car.
        setVehicle({ ...CUSTOMER_VEHICLE, id: 'guest', vehicle_id: 'guest' });
        loadTunes();
        return;
      }
      const list = user.vehicles || [];
      let v = list[0];
      if (!v) {
        // First-ever sign-in for this user — seed their default car via the
        // CORRECT endpoint so it persists across logouts. addVehicle() pushes
        // into db.users.vehicles[] and updates AuthContext state.
        try {
          v = await addVehicle({
            year: CUSTOMER_VEHICLE.year,
            make: CUSTOMER_VEHICLE.make,
            model: CUSTOMER_VEHICLE.model,
            engine: CUSTOMER_VEHICLE.engine,
            ecu_type: CUSTOMER_VEHICLE.ecu_type,
            vin: CUSTOMER_VEHICLE.vin,
            fuel_type: 'E85',
          });
        } catch (_e) {
          // Vehicle already exists server-side (stale client state) — refresh and reuse it
          const fresh = await checkAuth();
          v = (fresh?.vehicles || [])[0];
        }
      }
      if (v) {
        // Normalize: backend stores `vehicle_id`, downstream uses `vehicle.id`
        const normalized = { ...v, id: v.id || v.vehicle_id, vehicle_id: v.vehicle_id || v.id };
        setVehicle(normalized);
        loadDatalogs(normalized.id);
        loadTunes();
      } else {
        loadTunes();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error initializing vehicle:', error);
    }
  }, [user, addVehicle, checkAuth, loadDatalogs, loadTunes]);

  useEffect(() => {
    initializeVehicle();
  }, [initializeVehicle]);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!vehicle) {
      alert('Please add or select a vehicle in the Garage first, then try the upload again.');
      return;
    }
    // Android Chrome / file managers sometimes pass CSV files with .txt suffix
    // or no suffix at all. Accept anything that LOOKS like CSV by extension
    // OR by MIME type. Backend re-validates anyway.
    const nameLower = (file.name || '').toLowerCase();
    const mimeOk = (file.type || '').toLowerCase().includes('csv') || file.type === 'text/plain' || file.type === 'application/octet-stream' || file.type === '';
    const extOk = nameLower.endsWith('.csv') || nameLower.endsWith('.txt');
    if (!extOk && !mimeOk) {
      alert(`That doesn't look like a CSV datalog. Got "${file.name}" (${file.type || 'no MIME'}). Please pick a .csv file.`);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    // Rename to .csv on the way out if needed — server enforces .csv extension.
    const safeName = nameLower.endsWith('.csv') ? file.name : (file.name.replace(/\.txt$/i, '') + '.csv');
    const formData = new FormData();
    formData.append('file', file, safeName);
    let progressInterval = null;
    try {
      const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      progressInterval = setInterval(() => setUploadProgress(prev => Math.min(prev + 10, 90)), 100);
      const response = await fetch(`${API_URL}/api/datalogs/upload/${vehicle.id}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      setUploadProgress(100);
      if (response.ok) {
        const analysis = await response.json();
        // Hint the detail view to auto-run AI narrative (so the experience matches
        // pasting a CSV into the chat copilot — no extra click).
        setSelectedDatalog({ ...analysis, _auto_ai: true });
        loadDatalogs(vehicle.id);
        setActiveTab('analysis');
      } else {
        let msg = `Upload failed (HTTP ${response.status})`;
        try {
          const text = await response.text();
          if (text) {
            try { const err = JSON.parse(text); if (err?.detail) msg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail); }
            catch { msg = text.slice(0, 200); }
          }
        } catch (_parseErr) { /* keep default msg */ }
        if (response.status === 401) {
          msg = 'Your session expired. Please sign in again and retry the upload.';
        } else if (response.status === 413) {
          msg = 'CSV is too large (10 MB cap). Trim the log to the most relevant section and try again.';
        }
        alert(`Datalog upload failed: ${msg}`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error uploading datalog:', error);
      alert(`Datalog upload failed: ${error.message || 'network/connection error'}. On mobile data, try over Wi-Fi.`);
    }
    finally {
      if (progressInterval) clearInterval(progressInterval);
      setUploading(false);
      setUploadProgress(0);
    }
  }, [vehicle, loadDatalogs]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const n = (file.name || '').toLowerCase();
    if (n.endsWith('.csv') || n.endsWith('.txt') || (file.type || '').toLowerCase().includes('csv')) {
      handleFileUpload({ target: { files: [file] } });
    } else {
      alert(`Drop a .csv datalog. Got "${file.name}".`);
    }
  }, [handleFileUpload]);

  const handleDragOver = (e) => e.preventDefault();

  const scrollToPortal = () => {
    portalRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sortedTunes = useMemo(() => {
    return [...tunes].sort((a, b) => {
      const aStage = a.filename.includes('E85') ? 1 : 0;
      const bStage = b.filename.includes('E85') ? 1 : 0;
      if (aStage !== bStage) return bStage - aStage;
      return b.filename.localeCompare(a.filename);
    });
  }, [tunes]);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-['Inter']">
      <ContestBanner />
      <SiteHeader
        showPortal={true}
        onLogoClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onPortalClick={scrollToPortal}
        vehicle={vehicle}
        onShowLogin={() => setShowLogin(true)}
        onShowTopUp={() => setTopUpOpen(true)}
      />

      {/* Wallet top-up modal */}
      <TopUpModal open={topUpOpen} onOpenChange={setTopUpOpen} lowBalanceReason={topUpReason} />

      {/* Login Modal */}
      {showLogin && <LoginPage onClose={() => setShowLogin(false)} />}

      {/* Free Baseline Analysis Modal */}
      <FreeAnalysisModal
        isOpen={showFreeAnalysis}
        onClose={() => setShowFreeAnalysis(false)}
        onSignUpCta={() => setShowLogin(true)}
      />

      {/* Trial Expired Modal — shown when any 402 trial_expired bubbles up */}
      <TrialExpiredModal
        open={Boolean(trialExpired)}
        onClose={() => setTrialExpired(null)}
        payload={trialExpired}
      />

      {/* Floating Claude Copilot — context-aware, always available for signed-in users */}
      {user && (
        <ClaudeFAB
          activeTab={activeTab}
          user={user}
          vehicle={vehicle}
          tunes={tunes}
        />
      )}

      {/* Claim success banner — shown once after sign-up auto-imports free-analysis files */}
      {claimBanner && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[90] bg-emerald-500/15 border border-emerald-500/50 rounded-sm px-4 py-3 max-w-lg backdrop-blur-md shadow-2xl" data-testid="free-claim-banner">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-500/20 border border-emerald-500/40 rounded-sm flex items-center justify-center flex-shrink-0">
              <span className="text-emerald-400 text-lg">✓</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-emerald-300 font-bold font-['Outfit']">Your free analysis files are loaded.</p>
              <p className="text-xs text-zinc-300 mt-0.5">
                <span className="text-white font-['JetBrains_Mono']">{claimBanner.bin}</span> + {claimBanner.datalogs} datalog{claimBanner.datalogs !== 1 ? 's' : ''} ready for iterative tuning.
              </p>
            </div>
            <button
              onClick={() => setClaimBanner(null)}
              className="text-zinc-500 hover:text-white flex-shrink-0"
              data-testid="close-claim-banner"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ===== UNIVERSAL AI TUNER HOMEPAGE (9 sections) ===== */}
      <UniversalHome
        onUpload={() => setShowFreeAnalysis(true)}
        onDemo={() => setShowFreeAnalysis(true)}
        onPricing={scrollToPortal}
      />

      {/* ===== ABOUT (story-driven, premium) ===== */}
      <UniversalAbout onUpload={() => setShowFreeAnalysis(true)} />

      {/* ===== TUNING PORTAL ===== */}
      <section ref={portalRef} id="portal" className="border-t border-[#FF5E1A]/30 bg-[#050505]" data-testid="portal-section">
        {/* Portal header */}
        <div className="container mx-auto px-6 pt-16 pb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5E1A] mb-2 block">Tuning Portal</span>
              <h2 className="font-['Outfit'] font-bold text-2xl sm:text-3xl text-white tracking-tight">
                {vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Loading...'}
              </h2>
              {vehicle && (
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-zinc-500 font-['JetBrains_Mono']">ECU: {vehicle.ecu_type}</span>
                  <span className="text-xs text-zinc-600">|</span>
                  <span className="text-xs text-zinc-500 font-['JetBrains_Mono']">Engine: {vehicle.engine}</span>
                  <span className="text-xs text-zinc-600">|</span>
                  <span className="text-xs text-zinc-500 font-['JetBrains_Mono']">VIN: ...{vehicle.vin}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="container mx-auto px-6 pb-20">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="space-y-3" data-testid="tabs-container">
              {/* Group 1: E85 Tuning Workflow */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#FF5E1A] font-['JetBrains_Mono'] font-bold flex-shrink-0 w-32 flex items-center gap-1.5" data-testid="group-label-e85">
                  <span className="w-1.5 h-1.5 bg-[#FF5E1A] rounded-full shadow-[0_0_10px_rgba(255,94,26,0.8)]"></span>
                  E85 Tuning
                </span>
                <TabsList className="bg-gradient-to-r from-[#0d0603] via-[#111111] to-[#0d0603] border border-[#FF5E1A]/30 rounded-sm p-1.5 flex-wrap h-auto shadow-[0_0_30px_rgba(255,94,26,0.08)]" data-testid="e85-tabs">
                  <TabsTrigger value="dashboard" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-[#FF5E1A] hover:bg-[#FF5E1A]/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#FF5E1A] data-[state=active]:to-[#FF7A3D] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(255,94,26,0.5)] data-[state=active]:font-bold transition-all">
                    <Gauge className="w-4 h-4 mr-2" />Dashboard
                  </TabsTrigger>
                  <TabsTrigger value="upload" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-[#FF5E1A] hover:bg-[#FF5E1A]/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#FF5E1A] data-[state=active]:to-[#FF7A3D] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(255,94,26,0.5)] data-[state=active]:font-bold transition-all">
                    <Upload className="w-4 h-4 mr-2" />Upload
                  </TabsTrigger>
                  <TabsTrigger value="health" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(16,185,129,0.5)] data-[state=active]:font-bold transition-all">
                    <ShieldCheck className="w-4 h-4 mr-2" />Health Check
                  </TabsTrigger>
                  <TabsTrigger value="analysis" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-[#FF5E1A] hover:bg-[#FF5E1A]/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#FF5E1A] data-[state=active]:to-[#FF7A3D] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(255,94,26,0.5)] data-[state=active]:font-bold transition-all">
                    <Activity className="w-4 h-4 mr-2" />Analysis
                  </TabsTrigger>
                  <TabsTrigger value="tunes" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-[#FF5E1A] hover:bg-[#FF5E1A]/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#FF5E1A] data-[state=active]:to-[#FF7A3D] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(255,94,26,0.5)] data-[state=active]:font-bold transition-all">
                    <Download className="w-4 h-4 mr-2" />My Tunes
                  </TabsTrigger>
                  <TabsTrigger value="ai-iterate" data-testid="tab-ai-iterate" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(16,185,129,0.5)] data-[state=active]:font-bold transition-all">
                    <Sparkles className="w-4 h-4 mr-2" />AI Iterate
                  </TabsTrigger>
                  <TabsTrigger value="vw-flash" data-testid="tab-vw-flash" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-600 data-[state=active]:to-blue-800 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(59,130,246,0.5)] data-[state=active]:font-bold transition-all">
                    <Car className="w-4 h-4 mr-2" />VW Flash
                  </TabsTrigger>
                  {/* Flash ECU + Live Data hidden from production until reliable for non-admin users.
                      Admins keep access for testing/dev. */}
                  {user?.role === 'admin' && (
                    <>
                      <TabsTrigger value="flash" data-testid="tab-flash-ecu" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-red-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(239,68,68,0.5)] data-[state=active]:font-bold transition-all">
                        <Usb className="w-4 h-4 mr-2" />Flash ECU
                      </TabsTrigger>
                      <TabsTrigger value="live" data-testid="tab-live-data" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(16,185,129,0.5)] data-[state=active]:font-bold transition-all">
                        <Radio className="w-4 h-4 mr-2" />Live Data
                      </TabsTrigger>
                    </>
                  )}
                  <TabsTrigger value="compare" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-cyan-500 data-[state=active]:to-cyan-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(6,182,212,0.5)] data-[state=active]:font-bold transition-all">
                    <GitCompare className="w-4 h-4 mr-2" />Compare
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Group 2: TunerPro Portal — advanced tooling */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.25em] text-purple-400 font-['JetBrains_Mono'] font-bold flex-shrink-0 w-32 flex items-center gap-1.5" data-testid="group-label-portal">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full shadow-[0_0_10px_rgba(192,132,252,0.8)]"></span>
                  TunerPro Portal
                </span>
                <TabsList className="bg-gradient-to-r from-[#0d0618] via-[#111111] to-[#0d0618] border border-purple-500/30 rounded-sm p-1.5 flex-wrap h-auto shadow-[0_0_30px_rgba(192,132,252,0.08)]" data-testid="portal-tabs">
                  <TabsTrigger value="ai" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-[#FF5E1A] hover:bg-[#FF5E1A]/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#FF5E1A] data-[state=active]:to-[#FF7A3D] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(255,94,26,0.5)] data-[state=active]:font-bold transition-all">
                    <Bot className="w-4 h-4 mr-2" />AI Copilot
                  </TabsTrigger>
                  {(user?.beta_tester || user?.role === 'admin') && (
                    <TabsTrigger value="discover" data-testid="tab-discover" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(16,185,129,0.5)] data-[state=active]:font-bold transition-all">
                      <Telescope className="w-4 h-4 mr-2" />Map Discovery
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="xdf-library" data-testid="tab-xdf-library" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-cyan-500 data-[state=active]:to-cyan-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(6,182,212,0.5)] data-[state=active]:font-bold transition-all">
                    <BookOpen className="w-4 h-4 mr-2" />XDF Library
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Group 3: Account / Meta */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-['JetBrains_Mono'] font-bold flex-shrink-0 w-32 flex items-center gap-1.5" data-testid="group-label-account">
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full shadow-[0_0_10px_rgba(161,161,170,0.6)]"></span>
                  Account
                </span>
                <TabsList className="bg-gradient-to-r from-[#0a0a0a] via-[#111111] to-[#0a0a0a] border border-zinc-700/50 rounded-sm p-1.5 flex-wrap h-auto" data-testid="account-tabs">
                  <TabsTrigger value="help" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 data-[state=active]:bg-zinc-800 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(255,255,255,0.1)] data-[state=active]:font-bold transition-all">
                    <BookOpen className="w-4 h-4 mr-2" />Help
                  </TabsTrigger>
                  <TabsTrigger value="pricing" className="rounded-sm px-4 py-2 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(16,185,129,0.5)] data-[state=active]:font-bold transition-all">
                    <CreditCard className="w-4 h-4 mr-2" />Services
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            {/* ===== DASHBOARD TAB ===== */}
            <TabsContent value="dashboard" className="space-y-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-bold text-white font-['Outfit']">Dashboard</h2>
                <button
                  onClick={() => setShowFreeAnalysis(true)}
                  data-testid="dashboard-health-check-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white text-xs font-bold shadow-[0_0_16px_rgba(6,182,212,0.4)]"
                >
                  <span aria-hidden>🩺</span>
                  Run 3-File Health Check
                </button>
              </div>
              <CustomerDashboard />
              <MapContributionPanel />
              <TunerRequest context="dashboard" />
              {(user?.is_unlimited || user?.role === 'admin') && <KBAnalyticsPanel />}
              {user?.role === 'admin' && <PageViewsPanel />}
              {user?.role === 'admin' && <A2LMapSearchPanel />}
              {user?.role === 'admin' && <ModOrdersPanel />}
              {user?.role === 'admin' && <ExternalResourcesPanel />}
              {user?.role === 'admin' && <TesterActivityPanel />}
              {user?.role === 'admin' && <AdminBetaApplicationsPanel apiUrl={API_URL} token={localStorage.getItem('auth_token')} />}
              {user?.role === 'admin' && <BlogAdminPanel />}
              {user?.role === 'admin' && <CurationPanel vehicleId={vehicle?.id} />}
              {user && <RegisterDataloggerPanel user={user} vehicles={vehicle ? [vehicle] : []} />}
              {/* Quick Stats */}
              {(() => {
                // Derive peak HP + peak torque from the user's datalog history.
                // Backend uses several field names depending on version:
                //   analysis.hp.estimated_peak_hp / estimated_peak_torque + *_rpm   (current)
                //   analysis.hp_estimate.peak_hp / peak_torque + *_rpm              (DatalogAnalysisView legacy)
                //   analysis.hp.peak_hp_crank                                       (FreeAnalysisModal format)
                // Fall back to analysis.hp.stock_hp / stock_torque so the dashboard
                // always renders a useful baseline before the user uploads a WOT pull.
                let bestHp = 0, bestHpRpm = 0, bestTorque = 0, bestTorqueRpm = 0;
                let stockHp = 0, stockTq = 0;
                for (const d of datalogs) {
                  const hp = d?.analysis?.hp || d?.analysis?.hp_estimate || {};
                  const peakHp = Number(hp.peak_hp ?? hp.estimated_peak_hp ?? hp.peak_hp_crank ?? 0) || 0;
                  const peakTq = Number(hp.peak_torque ?? hp.estimated_peak_torque ?? 0) || 0;
                  if (peakHp > bestHp) { bestHp = peakHp; bestHpRpm = Number(hp.peak_hp_rpm) || 0; }
                  if (peakTq > bestTorque) { bestTorque = peakTq; bestTorqueRpm = Number(hp.peak_torque_rpm) || 0; }
                  // Capture stock baselines from the most recent log that has them
                  if (!stockHp && hp.stock_hp) stockHp = Number(hp.stock_hp);
                  if (!stockTq && hp.stock_torque) stockTq = Number(hp.stock_torque);
                }
                const hasMeasuredHp = bestHp > 0;
                const hasMeasuredTq = bestTorque > 0;
                const fmtHp = hasMeasuredHp ? bestHp.toFixed(0) : (stockHp ? stockHp.toFixed(0) : '—');
                const fmtTq = hasMeasuredTq ? bestTorque.toFixed(0) : (stockTq ? stockTq.toFixed(0) : '—');
                const hpSubText = hasMeasuredHp
                  ? (bestHpRpm > 0 ? `@ ${bestHpRpm.toFixed(0)} RPM` : 'measured')
                  : (stockHp ? 'stock baseline · upload a WOT log' : '');
                const tqSubText = hasMeasuredTq
                  ? (bestTorqueRpm > 0 ? `@ ${bestTorqueRpm.toFixed(0)} RPM` : 'measured')
                  : (stockTq ? 'stock baseline · upload a WOT log' : '');
                return (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                      <CardContent className="p-5">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Available Tunes</p>
                        <p className="text-3xl font-bold text-white font-['JetBrains_Mono']" data-testid="stat-tunes-count">{tunes.length}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                      <CardContent className="p-5">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Datalogs</p>
                        <p className="text-3xl font-bold text-white font-['JetBrains_Mono']" data-testid="stat-datalogs-count">{datalogs.length}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                      <CardContent className="p-5">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Peak HP</p>
                        <p className="text-3xl font-bold text-[#FF5E1A] font-['JetBrains_Mono']" data-testid="stat-peak-hp">
                          {fmtHp}<span className="text-lg text-zinc-500 ml-1">hp</span>
                        </p>
                        {hpSubText && <p className="text-[10px] font-['JetBrains_Mono'] text-zinc-500 mt-1">{hpSubText}</p>}
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                      <CardContent className="p-5">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Peak Torque</p>
                        <p className="text-3xl font-bold text-[#FF5E1A] font-['JetBrains_Mono']" data-testid="stat-peak-torque">
                          {fmtTq}<span className="text-lg text-zinc-500 ml-1">ft-lbs</span>
                        </p>
                        {tqSubText && <p className="text-[10px] font-['JetBrains_Mono'] text-zinc-500 mt-1">{tqSubText}</p>}
                      </CardContent>
                    </Card>
                    <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                      <CardContent className="p-5">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Current Stage</p>
                        <p className="text-3xl font-bold text-emerald-400 font-['JetBrains_Mono']">Stage 2</p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {/* Vehicle Info Card */}
              {vehicle && (
                <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
                      <Car className="w-5 h-5 text-[#FF5E1A]" />Vehicle Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: 'Year', value: vehicle.year },
                        { label: 'Make/Model', value: `${vehicle.make} ${vehicle.model}` },
                        { label: 'Engine', value: vehicle.engine },
                        { label: 'ECU', value: vehicle.ecu_type },
                      ].map(item => (
                        <div key={item.label} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                          <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">{item.label}</p>
                          <p className="text-sm text-white font-medium">{item.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4">
                      <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">Installed Modifications</p>
                      <div className="flex flex-wrap gap-2">
                        {vehicle.mods?.map(mod => (
                          <span key={mod} className="px-2 py-1 text-xs bg-[#FF5E1A]/10 border border-[#FF5E1A]/20 text-[#FF5E1A] rounded-sm">{mod}</span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent activity */}
              <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
                    <Clock className="w-5 h-5 text-[#FF5E1A]" />Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { action: "Generated E85 V11 Tune", detail: "Cal-block fuel map scaling: +50% fuel via Load & Main maps", time: "Latest" },
                      { action: "Generated E85 V10 Tune", detail: "Reverted adaptation limits, cured limp mode", time: "Previous" },
                      { action: "Analyzed datalog V8 Pull2", detail: "WOT AFR 14.0 (open-loop), ECU ignoring c_mff_fac", time: "Previous" },
                    ].map((item) => (
                      <div key={item.action} className="flex items-start gap-3 p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                        <div className="w-2 h-2 mt-2 rounded-full bg-[#FF5E1A] flex-shrink-0" />
                        <div>
                          <p className="text-sm text-white font-medium">{item.action}</p>
                          <p className="text-xs text-zinc-500">{item.detail}</p>
                        </div>
                        <span className="text-xs text-zinc-600 ml-auto flex-shrink-0">{item.time}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== UPLOAD TAB ===== */}
            <TabsContent value="upload" className="space-y-6">
              <TunerRequest context="upload" />
              <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
                    <Upload className="w-5 h-5 text-[#FF5E1A]" />Upload Datalog
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Upload a CSV datalog for AI analysis. Drag and drop or click to browse.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-dashed border-[#27272A] hover:border-[#FF5E1A]/50 rounded-sm p-12 text-center transition-colors cursor-pointer"
                    onClick={() => document.getElementById('file-upload')?.click()}
                    data-testid="upload-drop-zone"
                  >
                    <Upload className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-white font-medium mb-1">Drag & drop your CSV datalog here</p>
                    <p className="text-sm text-zinc-500">or click to browse files</p>
                    <input id="file-upload" type="file" accept=".csv,.txt,text/csv,text/plain,application/vnd.ms-excel,application/octet-stream" onChange={handleFileUpload} className="hidden" data-testid="upload-file-input" />
                  </div>
                  {uploading && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white">Uploading & analyzing...</span>
                        <span className="text-[#FF5E1A] font-['JetBrains_Mono']">{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="h-1" />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Previous Datalogs */}
              {datalogs.length > 0 && (
                <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                  <CardHeader>
                    <CardTitle className="text-white font-['Outfit']">Previous Datalogs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {datalogs.map((log) => (
                        <div key={log.id} className={`flex items-center justify-between p-3 bg-[#050505] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/30 transition-colors cursor-pointer ${log.hidden ? 'opacity-40' : ''}`}
                          onClick={() => { setSelectedDatalog(log); setActiveTab('analysis'); }}
                          data-testid={`datalog-${log.id}`}>
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-[#FF5E1A]" />
                            <div>
                              <p className="text-sm text-white flex items-center gap-2">
                                {log.filename}
                                {log.hidden && <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 bg-zinc-700/40 text-zinc-400 border border-zinc-600/40 rounded-sm">Hidden</span>}
                              </p>
                              <p className="text-xs text-zinc-600">{log.timestamp}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              title="Delete this datalog permanently"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete datalog "${log.filename}"?\n\nThis removes the CSV + all derived stats. Cannot be undone.`)) return;
                                try {
                                  const token = localStorage.getItem('auth_token');
                                  const res = await fetch(`${API_URL}/api/datalogs/${log.id}`, {
                                    method: 'DELETE',
                                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                                  });
                                  if (!res.ok) { const d = await res.json().catch(() => ({})); window.alert(`Delete failed: ${d.detail || res.statusText}`); return; }
                                  if (vehicle) loadDatalogs(vehicle.id);
                                } catch (err) { window.alert(`Delete failed: ${err.message}`); }
                              }}
                              className="p-1 rounded-sm text-red-500/70 hover:text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-datalog-${log.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            {user?.role === 'admin' && (
                              <button
                                type="button"
                                title={log.hidden ? 'Unhide datalog' : 'Hide datalog from your timeline'}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
                                    const res = await fetch(`${API_URL}/api/admin/datalogs/${log.id}/visibility`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                      body: JSON.stringify({ hidden: !log.hidden }),
                                    });
                                    if (res.ok && vehicle) loadDatalogs(vehicle.id);
                                  } catch (_err) { /* silent */ }
                                }}
                                className={`p-1 rounded-sm hover:bg-[#1a1a1a] ${log.hidden ? 'text-zinc-500' : 'text-zinc-600 hover:text-zinc-300'}`}
                                data-testid={`toggle-datalog-hidden-${log.id}`}
                              >
                                {log.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            )}
                            <ArrowRight className="w-4 h-4 text-zinc-600" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Binary Upload */}
              <BinUpload onUploadComplete={() => loadTunes()} />

            {/* ===== HEALTH CHECK TAB ===== */}
            <TabsContent value="health" className="space-y-6" data-testid="health-tab">
              <HealthCheck onProceed={() => setActiveTab('ai')} />
            </TabsContent>

            {/* ===== ANALYSIS TAB ===== */}
            </TabsContent>
            <TabsContent value="analysis" className="space-y-6">
              <TunerRequest context="analysis" />
              {selectedDatalog ? (
                <>
                  <GenerateTuneFromDatalog
                    datalog={selectedDatalog}
                    onSuccess={() => loadTunes(vehicle?.id)}
                  />
                  <DatalogAnalysisView datalog={selectedDatalog} />
                  <VanosScorecard datalog={selectedDatalog} datalogs={datalogs} />
                  <DatalogCharts datalog={selectedDatalog} />
                </>
              ) : (
                <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                  <CardContent className="p-12 text-center">
                    <Activity className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-white font-medium mb-1">No datalog selected</p>
                    <p className="text-sm text-zinc-500 mb-6">Upload a datalog or select from your history</p>
                    <Button onClick={() => setActiveTab('upload')} className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm">
                      <Upload className="w-4 h-4 mr-2" />Upload Datalog
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ===== AI ITERATE TAB — upload current bin + datalog, get next tune ===== */}
            <TabsContent value="ai-iterate" className="space-y-6">
              <AITuneIterator />
            </TabsContent>

            {/* ===== VW FLASH PREP TAB — analyze VW ECU dump + get local flash command ===== */}
            <TabsContent value="vw-flash" className="space-y-6">
              <VWFlashPrep />
            </TabsContent>


            {/* ===== MY TUNES TAB ===== */}
            <TabsContent value="tunes" className="space-y-6">
              <TunerRequest context="tunes" />

              <TuneRecommender />

              {/* Tune Version Timeline */}
              <TuneTimeline />

              {/* Featured latest tune */}
              {/* ECU Recovery Card */}
              <Card className="bg-[#111111] border border-yellow-500/40 rounded-sm" data-testid="recovery-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <CircleAlert className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-bold font-['Outfit'] text-sm">ECU RECOVERY</span>
                  </div>
                  <p className="text-xs text-zinc-300 mb-3">If BTT shows "ECU unlock not detected" or "Signature check error", flash the stock backup first using Flash Full.</p>
                  <Button variant="outline" className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 rounded-sm text-xs"
                    onClick={() => downloadFile(`${API_URL}/api/bin/download/WBAPK5C58AA652287_MSV80.1_0049RK0MK80S_fullread2.bin`, 'STOCK_RECOVERY.bin')}
                    data-testid="download-stock-recovery">
                    <Download className="w-4 h-4 mr-2" />Download Stock Recovery BIN
                  </Button>
                </CardContent>
              </Card>

              {/* Featured latest tune - V11 */}
              <Card className="bg-[#111111] border border-[#FF5E1A]/40 rounded-sm" data-testid="featured-tune-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-[#FF5E1A]" />
                    <span className="text-[#FF5E1A] font-['Outfit']">LATEST</span>
                    <span className="text-white font-['Outfit']">Stage 2 E85 — Fuel Map Scaling</span>
                  </CardTitle>
                  <CardDescription className="text-zinc-300">
                    NEW STRATEGY: Direct fuel map scaling inside cal block. ECU was ignoring c_mff_fac — V11 scales fuel delivery maps by 1.50x instead.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                      <p className="text-sm font-bold text-[#FF5E1A] mb-2 font-['Outfit']">V11 Fuel Strategy</p>
                      <ul className="text-xs text-zinc-300 space-y-1.5">
                        <li className="text-emerald-400 font-bold font-['JetBrains_Mono']">Load Fuel Map: x1.50 (192 cells scaled)</li>
                        <li className="text-emerald-400 font-bold font-['JetBrains_Mono']">Main Fuel Map: x1.50 (200 cells scaled)</li>
                        <li>c_mff_fac: 1/14.7 to 1/9.8 (backup method)</li>
                        <li className="text-cyan-400">ALL 800 bytes within cal block only</li>
                        <li className="text-cyan-400">Safe for Flash Tune OR Flash Full</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                      <p className="text-sm font-bold text-red-400 mb-2 font-['Outfit']">Kickdown Disable</p>
                      <ul className="text-xs text-zinc-300 space-y-1.5">
                        <li>DME kickdown threshold zeroed (12 addresses)</li>
                        <li>Manual gear hold at WOT</li>
                        <li className="text-emerald-400">Adaptation limits PRESERVED (no limp mode)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm"
                      onClick={() => user
                        ? downloadWithCredit('BMW_E90_328xi_Stage2_E85_278HP.bin', user, () => checkAuth())
                        : downloadFile(`${API_URL}/api/bin/download/BMW_E90_328xi_Stage2_E85_278HP.bin`, 'BMW_E90_328xi_Stage2_E85_278HP.bin')
                      }
                      data-testid="download-featured-e85-v11">
                      <Download className="w-4 h-4 mr-2" />Download E85 V11{user ? ' (1 credit)' : ''}
                    </Button>
                    <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-sm"
                      onClick={() => { setSelectedTuneForFlash({ filename: 'BMW_E90_328xi_Stage2_E85_278HP.bin', download_url: '/api/bin/download/BMW_E90_328xi_Stage2_E85_278HP.bin' }); setActiveTab('flash'); }}
                      data-testid="flash-featured-e85-v11">
                      <Usb className="w-4 h-4 mr-2" />Flash This Tune
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* All tunes */}
              <Card className="bg-[#111111] border-[#27272A] rounded-sm">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
                        <Download className="w-5 h-5 text-[#FF5E1A]" />All Available Tunes
                      </CardTitle>
                      <CardDescription className="text-zinc-400">
                        Download tune files for your {vehicle?.year} {vehicle?.make} {vehicle?.model}
                      </CardDescription>
                    </div>
                    {user?.role === 'admin' && (
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 rounded-sm text-xs"
                          data-testid="build-next-revision-btn"
                          onClick={async () => {
                            if (!window.confirm(
                              'Build Next Revision (AI)?\n\n' +
                              '• Reads your latest valid datalog\n' +
                              '• Calculates mid-range AFR delta vs target (11.5 E85 / 12.8 gas)\n' +
                              '• Sizes enrichment % from delta (capped at +55%)\n' +
                              '• Adds timing pull if knock peak > 1.2V\n' +
                              '• Generates new bin with reasoning shown\n\n' +
                              'Refuses to build if no valid datalog or current tune still target-met.'
                            )) return;
                            try {
                              const token = localStorage.getItem('auth_token');
                              const r = await fetch(`${API_URL}/api/tunes/build-next-revision`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const d = await r.json();
                              if (!r.ok) { window.alert(`Error: ${d.detail || r.statusText}`); return; }
                              const dedupedMsg = d.deduped ? '\n\n⚠️ DEDUPED — identical bytes existed already.' : '\n\nNew bin: ' + d.clean_filename;
                              window.alert(
                                `AI-built ${d.version}\n\n` +
                                `Source log: ${d.source_datalog || '?'}\n` +
                                `From tune: ${d.source_tune || '?'}\n` +
                                `Base bin: ${d.base}\n\n` +
                                `AI reasoning:\n${d.ai_reasoning || '(none)'}\n\n` +
                                `Bytes changed: ${d.total_bytes_changed_vs_base}\n` +
                                `  · ${d.enrichment_cells_scaled} fuel @ ×${d.scaling_strategy?.enrichment_scale}\n` +
                                `  · ${d.timing_cells_pulled} timing @ ${d.scaling_strategy?.timing_actual_delta_deg}°\n` +
                                `sha256: ${(d.sha256||'').slice(0,16)}…` +
                                dedupedMsg
                              );
                              window.location.reload();
                            } catch (e) {
                              window.alert(`Failed: ${e.message}`);
                            }
                          }}
                        >
                          <Bot className="w-3 h-3 mr-1.5" />Build Next Revision (AI)
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm text-xs"
                          data-testid="build-idle-target-btn"
                          onClick={async () => {
                            const tgt = window.prompt(
                              'Build Idle AFR Target tune\n\n' +
                              'Base: V12_Safe (preserves WOT + timing pull)\n' +
                              'Scales idle band of main fuel map (0x049744)\n' +
                              'Floor clamp at 8000 — no stall risk\n\n' +
                              'Target idle AFR? (e.g., 12.5, 13.0, 13.5):',
                              '12.5'
                            );
                            if (!tgt) return;
                            const targetAfr = parseFloat(tgt);
                            if (!(targetAfr >= 11.0 && targetAfr <= 15.5)) {
                              window.alert('Target AFR must be between 11.0 and 15.5');
                              return;
                            }
                            const curRaw = window.prompt(
                              'What is V12_Safe producing today at idle? (gas-scaled AFR)\n\n' +
                              'Default 10.5 (last reported). Lower = richer.',
                              '10.5'
                            );
                            if (!curRaw) return;
                            const currentAfr = parseFloat(curRaw);
                            if (!(currentAfr >= 9.0 && currentAfr <= 13.0)) {
                              window.alert('Current AFR must be between 9.0 and 13.0');
                              return;
                            }
                            if (currentAfr >= targetAfr) {
                              window.alert('Target AFR must be LEANER (higher number) than current. This tool only leans.');
                              return;
                            }
                            try {
                              const token = localStorage.getItem('auth_token');
                              const r = await fetch(`${API_URL}/api/tunes/generate/v12-safe-idle-target`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ target_afr: targetAfr, current_afr: currentAfr, col_max: 4 }),
                              });
                              const d = await r.json();
                              if (!r.ok) { window.alert(`Error: ${d.detail || r.statusText}`); return; }
                              const dedupedMsg = d.deduped ? '\n\n⚠️ DEDUPED — identical bytes existed already.\nUsing existing bin: ' + d.filename : '\n\nNew bin: ' + d.clean_filename;
                              window.alert(
                                `${d.version} built\n\n` +
                                `Target idle AFR: ${targetAfr}\n` +
                                `Assumed current: ${currentAfr}\n` +
                                `Scale factor: ${d.scaling_strategy?.idle_lean_scale?.toFixed(3)} (${d.scaling_strategy?.idle_lean_pct})\n` +
                                `Cells leaned: ${d.idle_cells_leaned}\n` +
                                `Bytes changed: ${d.total_bytes_changed_vs_base}\n` +
                                `sha256: ${(d.sha256||'').slice(0,16)}…` +
                                dedupedMsg
                              );
                              window.location.reload();
                            } catch (e) {
                              window.alert(`Failed: ${e.message}`);
                            }
                          }}
                        >
                          <Bot className="w-3 h-3 mr-1.5" />Idle AFR Target
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 rounded-sm text-xs"
                          data-testid="idle-auto-from-datalog-btn"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('auth_token');
                              // Step 1 — preview (reads latest datalog, computes recommendation)
                              const r1 = await fetch(`${API_URL}/api/tunes/generate/v12-safe-idle-auto`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ confirm: false }),
                              });
                              const d1 = await r1.json();
                              if (!r1.ok) { window.alert(`Error: ${d1.detail || r1.statusText}`); return; }
                              const msg = (
                                'AI read your latest datalog.\n\n' +
                                `Source: ${d1.source_datalog}\n` +
                                `Fuel: ${d1.fuel}\n` +
                                `Measured idle AFR: ${d1.current_idle_afr} (range ${d1.afr_min}-${d1.afr_max}, n=${d1.idle_samples})\n` +
                                `Recommended target: ${d1.recommended_target_afr} AFR\n\n` +
                                `${d1.rationale}\n\n` +
                                'Build this tune now?'
                              );
                              if (!window.confirm(msg)) return;
                              // Step 2 — confirm build
                              const r2 = await fetch(`${API_URL}/api/tunes/generate/v12-safe-idle-auto`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ confirm: true, target_afr: d1.recommended_target_afr }),
                              });
                              const d2 = await r2.json();
                              if (!r2.ok) { window.alert(`Error: ${d2.detail || r2.statusText}`); return; }
                              const t = d2.tune || {};
                              const dedupedMsg = t.deduped ? '\n\n⚠️ DEDUPED — identical bytes existed already.\nUsing: ' + t.filename : '\n\nNew bin: ' + t.clean_filename;
                              window.alert(
                                `${t.version || 'Tune'} built\n\n` +
                                `Target idle AFR: ${d2.recommended_target_afr}\n` +
                                `Measured: ${d2.current_idle_afr}\n` +
                                `Scale: ${(t.scaling_strategy?.idle_lean_scale || 0).toFixed(3)} (${t.scaling_strategy?.idle_lean_pct || ''})\n` +
                                `Cells: ${t.idle_cells_leaned}\n` +
                                `Checksum: ${t.checksum_repair?.ok ? 'repaired against V9 ✓' : 'unrepaired'}\n` +
                                `sha256: ${(t.sha256 || '').slice(0, 16)}…` +
                                dedupedMsg
                              );
                              window.location.reload();
                            } catch (e) {
                              window.alert(`Failed: ${e.message}`);
                            }
                          }}
                        >
                          <Bot className="w-3 h-3 mr-1.5" />Auto Idle (from log)
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 rounded-sm text-xs"
                          data-testid="generate-v12b-btn"
                          onClick={async () => {
                            if (!window.confirm('Generate V12b_AggroMid?\n\n• Base: V12_550_MidExtend\n• +45% enrichment on rows 10-13 (was +30%)\n• -2.25° timing on rows 12-15 for E85 knock margin\n• Auto-dedupes if identical bytes already exist')) return;
                            try {
                              const token = localStorage.getItem('auth_token');
                              const r = await fetch(`${API_URL}/api/tunes/generate/v12b-aggromid`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const d = await r.json();
                              if (!r.ok) { window.alert(`Error: ${d.detail || r.statusText}`); return; }
                              const dedupedMsg = d.deduped ? '\n\n⚠️ DEDUPED — identical bytes existed already.\nUsing existing bin: ' + d.filename : '\n\nNew bin: ' + d.clean_filename;
                              window.alert(
                                `V12b_AggroMid generated\n\n` +
                                `Base: ${d.base}\n` +
                                `Bytes changed: ${d.total_bytes_changed_vs_base}\n` +
                                `  · ${d.enrichment_cells_scaled} fuel cells\n` +
                                `  · ${d.timing_cells_pulled} timing cells\n` +
                                `sha256: ${(d.sha256||'').slice(0,16)}…\n` +
                                `safety_ok: ${d.safety_ok}` +
                                dedupedMsg
                              );
                              window.location.reload();
                            } catch (e) {
                              window.alert(`Failed: ${e.message}`);
                            }
                          }}
                        >
                          Generate V12b_AggroMid
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 rounded-sm text-xs"
                          data-testid="admin-dedupe-bins-btn"
                          onClick={async () => {
                            if (!window.confirm('Dedupe duplicate bins? Groups by sha256, keeps the oldest per group, deletes the rest from disk + GridFS + db. Safe — bytes-identical files only.')) return;
                            try {
                              const token = localStorage.getItem('auth_token');
                              const r = await fetch(`${API_URL}/api/admin/bin/dedupe`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const d = await r.json();
                              if (!r.ok) { window.alert(`Error: ${d.detail || r.statusText}`); return; }
                              window.alert(`Dedupe complete.\nGroups scanned: ${d.groups_scanned}\nKept: ${d.kept.length}\nRemoved: ${d.removed.length}\n\n${d.removed.slice(0, 10).join('\n')}${d.removed.length > 10 ? `\n... and ${d.removed.length - 10} more` : ''}`);
                              window.location.reload();
                            } catch (e) {
                              window.alert(`Failed: ${e.message}`);
                            }
                          }}
                        >
                          Admin: Dedupe Bins
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 rounded-sm text-xs"
                          data-testid="admin-repair-checksums-btn"
                          onClick={async () => {
                            if (!window.confirm(
                              'Repair MSV80 cal-block checksums on every .bin?\n\n' +
                              '• Compares each tune to V9 base\n' +
                              '• Adjusts compensator word at 0x053DEC so the 16-bit BE word sum matches V9\n' +
                              '• Flips status from "cal_block_modified" → "cal_block_sum_preserved" on every UI card\n' +
                              '• Safe — only writes the 2-byte compensator, never touches your fuel/timing tables\n' +
                              '• Updates DB sha256 + GridFS\n\n' +
                              'After this, your tunes flash without needing WinOLS / external checksum tools.'
                            )) return;
                            try {
                              const token = localStorage.getItem('auth_token');
                              const r = await fetch(`${API_URL}/api/admin/bin/repair-checksums`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const d = await r.json();
                              if (!r.ok) { window.alert(`Error: ${d.detail || r.statusText}`); return; }
                              window.alert(
                                `Checksum repair complete.\n\n` +
                                `Scanned: ${d.scanned}\n` +
                                `Repaired: ${d.repaired_count}\n` +
                                `Already clean: ${d.already_clean_count}\n` +
                                `Skipped: ${d.skipped_count}\n\n` +
                                `Base reference: ${d.base_reference}\n\n` +
                                `First few repaired:\n${(d.repaired_files || []).slice(0, 8).join('\n')}` +
                                (d.repaired_count > 8 ? `\n... and ${d.repaired_count - 8} more` : '')
                              );
                              window.location.reload();
                            } catch (e) {
                              window.alert(`Failed: ${e.message}`);
                            }
                          }}
                        >
                          Admin: Repair Checksums
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sortedTunes.map((tune) => {
                      const isV11 = tune.filename === 'BMW_E90_328xi_Stage2_E85_278HP.bin';
                      // "Currently Flashed" badge — sha256 match OR filename mentioned in vehicle.current_tune_version
                      const flashedTune = String(vehicle?.current_tune_version || '');
                      const isFlashed = (vehicle?.last_flash_sha256 && tune.sha256 && vehicle.last_flash_sha256 === tune.sha256)
                        || (flashedTune && (flashedTune.includes(tune.filename) || (tune.version && flashedTune.toUpperCase().includes(String(tune.version).replace(/_/g,'')))));
                      return (
                        <div key={tune.filename}
                          className={`flex items-center justify-between p-4 rounded-sm border ${
                            isFlashed ? 'bg-emerald-500/5 border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.15)]' :
                            isV11 ? 'bg-[#FF5E1A]/5 border-[#FF5E1A]/30' : 'bg-[#050505] border-[#27272A]'
                          }`}
                          data-testid={`tune-row-${tune.filename}`}>
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className={`p-2 rounded-sm ${isFlashed ? 'bg-emerald-500/20' : isV11 ? 'bg-[#FF5E1A]/20' : 'bg-[#1A1A1A]'}`}>
                              <Flame className={`w-5 h-5 ${isFlashed ? 'text-emerald-400' : isV11 ? 'text-[#FF5E1A]' : 'text-zinc-500'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-white text-sm font-['JetBrains_Mono'] truncate">
                                {tune.filename}
                                {isFlashed && <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-sm" data-testid="flashed-badge">FLASHED</span>}
                                {isV11 && !isFlashed && <span className="ml-2 text-xs bg-[#FF5E1A]/20 text-[#FF5E1A] px-2 py-0.5 rounded-sm">LATEST</span>}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-zinc-500 mt-0.5 font-['JetBrains_Mono']">
                                <span>{tune.size_kb} KB</span>
                                {tune.version && <span className="text-emerald-400">{tune.version}</span>}
                                {tune.sha256 && (
                                  <span title={tune.sha256}>sha:{String(tune.sha256).slice(0, 10)}</span>
                                )}
                                {tune.created_at && (
                                  <span>{new Date(tune.created_at).toLocaleDateString()} {new Date(tune.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                                )}
                                {tune.base && <span className="truncate max-w-[200px]" title={`base: ${tune.base}`}>← {String(tune.base).replace('BMW_E90_328xi_E85_','').replace('.bin','')}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm text-xs"
                              onClick={() => { setSelectedTuneForFlash(tune); setActiveTab('flash'); }}
                              data-testid={`flash-${tune.filename}`}>
                              <Usb className="w-3.5 h-3.5 mr-1.5" />Flash
                            </Button>
                            <Button variant="outline" className="border-[#FF5E1A]/30 text-[#FF5E1A] hover:bg-[#FF5E1A]/10 rounded-sm text-xs"
                              onClick={() => user
                                ? downloadWithCredit(tune.filename, user, () => checkAuth())
                                : downloadFile(`${API_URL}${tune.download_url}`, tune.filename)
                              }
                              data-testid={`download-${tune.filename}`}>
                              <Download className="w-3.5 h-3.5 mr-1.5" />{user ? '1 cr' : 'Download'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* XDF Definition Files card intentionally hidden — moved behind beta-only flag.
                  To re-enable for all users, restore the XDFFileList card here. */}
            </TabsContent>

            {/* ===== FLASH ECU TAB ===== (admin-only — hidden from production until reliable) */}
            {user?.role === 'admin' && (
              <TabsContent value="flash" className="space-y-6" data-testid="tab-content-flash">
                <TunerRequest context="flash" />
                <FlashECUTab vehicle={vehicle} tunes={tunes} selectedTune={selectedTuneForFlash} onSelectTune={setSelectedTuneForFlash} />
              </TabsContent>
            )}

            {/* ===== LIVE DATA TAB ===== (admin-only — hidden from production until reliable) */}
            {user?.role === 'admin' && (
              <TabsContent value="live" className="space-y-6" data-testid="tab-content-live">
                <TunerRequest context="live" />
                <LiveDataLogger vehicle={vehicle} />
              </TabsContent>
            )}

            {/* ===== COMPARE TAB ===== */}
            <TabsContent value="compare" className="space-y-6">
              <TunerRequest context="compare" />
              <TuneCompare tunes={tunes} />
            </TabsContent>

            <TabsContent value="ai" className="space-y-6" data-testid="ai-tab">
              {user && ((user.credits || 0) > 0 || user.is_unlimited || user.role === 'admin') ? (
                <AIAssistant vehicle={vehicle} />
              ) : (
                <>
                  <PaywallGate feature="AI Tuning Copilot" user={user} onUpgrade={() => setActiveTab('pricing')} />
                  <AIAssistant vehicle={vehicle} />
                </>
              )}
            </TabsContent>

            {(user?.beta_tester || user?.role === 'admin') && (
              <TabsContent value="discover" className="space-y-6" data-testid="discover-tab">
                <MapDiscoveryPage />
              </TabsContent>
            )}

            <TabsContent value="xdf-library" className="space-y-6" data-testid="xdf-library-tab">
              <XDFLibraryPanel />
            </TabsContent>

            <TabsContent value="help" className="space-y-6" data-testid="help-tab">
              <MechanicalGuide />
            </TabsContent>

            <TabsContent value="pricing" className="space-y-6">
              <div className="bg-gradient-to-b from-[#0F0F0F] to-[#0A0A0A] border border-[#27272A] rounded-sm p-8 sm:p-12 text-center" data-testid="services-pricing-redirect">
                <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-[#FF5E1A] mb-3">// Tuning Services</div>
                <h2 className="font-['Outfit'] text-2xl sm:text-4xl font-black tracking-tight text-white mb-3">
                  One price. All revisions included.
                </h2>
                <p className="text-sm sm:text-base text-zinc-400 max-w-xl mx-auto mb-7">
                  Single Tune $250 · Dual Tune $450 · Shop Pack $1,500. Bundles with the Smart Datalogger and AI Tuner Pro Flasher are also available. Unlimited revisions until YOU Green Light the tune.
                </p>
                <a
                  href="/green-light#pricing"
                  data-testid="services-view-pricing-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all shadow-[0_0_24px_rgba(255,94,26,0.35)]"
                >
                  View Pricing &amp; Checkout
                </a>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <Footer />

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-24 w-10 h-10 bg-[#111111] border border-[#27272A] hover:border-[#FF5E1A]/50 flex items-center justify-center rounded-sm transition-colors z-40"
        data-testid="back-to-top-btn"
      >
        <ChevronUp className="w-5 h-5 text-zinc-400" />
      </button>
    </div>
  );
}


// XDFFileList intentionally removed from public UI.
// Original component lives in git history — restore if re-enabling XDF downloads for all users.

// ===== TUNER REQUEST COMPONENT =====
function TunerRequest({ context }) {
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = () => {
    const text = message.trim();
    if (!text) return;
    // Hand off to the AI copilot — real chat instead of a dead form.
    const prefixed =
      `[Quick Tuner Request from the "${context || 'portal'}" tab] ${text}\n\n` +
      `If this sounds mechanical but could also be a tuning issue (cold-start enrichment, ` +
      `warmup fueling, ignition retard during warmup, cranking mixture, etc.), please ` +
      `consider BOTH mechanical and tuning root causes before narrowing down.`;
    if (window.tunerProChat?.open) {
      window.tunerProChat.open(prefixed);
      setSent(true);
      setMessage('');
      setTimeout(() => setSent(false), 3500);
    } else {
      alert('AI Copilot is still loading — try again in a moment.');
    }
  };

  return (
    <Card className="bg-[#0A0A0A] border-[#27272A] rounded-sm">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-[#FF5E1A] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-white font-medium mb-2 font-['Outfit']">Quick Tuner Request</p>
            {sent ? (
              <p className="text-sm text-emerald-400" data-testid="tuner-request-sent">
                Opened in AI Copilot — check the chat window at the bottom right.
              </p>
            ) : (
              <div className="flex gap-2">
                <Textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
                  placeholder={`Describe your ${context} request... (e.g. "pops through intake on cold start")`}
                  className="bg-[#050505] border-[#27272A] text-white text-sm min-h-[40px] resize-none rounded-sm focus:border-[#FF5E1A]"
                  rows={1}
                  data-testid="tuner-request-textarea"
                />
                <Button
                  onClick={handleSubmit}
                  disabled={!message.trim()}
                  className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm flex-shrink-0"
                  data-testid="tuner-request-submit"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#050505', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', padding: '2rem' }}>
          <div style={{ width: 48, height: 48, background: 'rgba(255,94,26,0.2)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
            <span style={{ color: '#FF5E1A', fontSize: 24 }}>!</span>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ color: '#71717a', fontSize: 14, marginBottom: 24 }}>The app encountered an error. Try refreshing the page.</p>
          <button onClick={() => { localStorage.clear(); window.location.reload(); }}
            style={{ padding: '12px 24px', background: '#FF5E1A', color: '#fff', border: 'none', borderRadius: 2, fontWeight: 700, fontSize: 13, cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Clear Cache &amp; Reload
          </button>
          {this.state.error && (
            <pre style={{ marginTop: 24, padding: 16, background: '#111', border: '1px solid #27272a', borderRadius: 2, fontSize: 11, color: '#ef4444', maxWidth: 600, overflow: 'auto' }}>
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default function WrappedApp() {
  if (typeof window !== 'undefined') window.__APP_BUILD = '20260413v2';
  // Fire a page-view ping on every route change (admin analytics panel reads this).
  usePageViewTracker();

  // Lightweight path-based routing — /green-light renders the standalone
  // Green Light Tune marketing/conversion page. No auth or app state needed.
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (pathname === '/green-light' || pathname === '/green-light/' || pathname === '/greenlight') {
    return (
      <ErrorBoundary>
        <GreenLightTune />
      </ErrorBoundary>
    );
  }

  if (pathname === '/datalogger' || pathname === '/datalogger/' || pathname === '/smart-datalogger') {
    return (
      <ErrorBoundary>
        <SmartDataloggerPage />
      </ErrorBoundary>
    );
  }

  if (
    pathname === '/megasquirt-diagnose' ||
    pathname === '/megasquirt-diagnose/' ||
    pathname === '/megasquirt' ||
    pathname === '/megasquirt/'
  ) {
    return (
      <ErrorBoundary>
        <MegaSquirtDiagnose />
      </ErrorBoundary>
    );
  }

  if (pathname === '/vote' || pathname === '/vote/') {
    return (
      <ErrorBoundary>
        <VoteSharePage />
      </ErrorBoundary>
    );
  }

  if (pathname === '/story' || pathname === '/story/') {
    return (
      <ErrorBoundary>
        <StoryPage />
      </ErrorBoundary>
    );
  }

  if (pathname === '/blog' || pathname === '/blog/') {
    return (
      <ErrorBoundary>
        <BlogListPage />
      </ErrorBoundary>
    );
  }

  // /blog/:slug
  const blogMatch = pathname.match(/^\/blog\/([^/]+)\/?$/);
  if (blogMatch) {
    return (
      <ErrorBoundary>
        <BlogPostPage slug={decodeURIComponent(blogMatch[1])} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ErrorBoundary>
  );
}
// Build: 1776145815
